import { DefaultButton, Icon, INavLink, INavLinkGroup, Nav, Stack } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../src/taskpane/components/Loader";
import { RootState } from "../../../src/taskpane/components/_v2/redux/storeMain";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { IMailFolder } from "../../../src/taskpane/interfaces/IMailFolder";
import { IPageSettings } from "../../../src/taskpane/interfaces/IPageSettings";
import { IWellKnownFolder } from "../../../src/taskpane/interfaces/IWellKnownFolder";
import { setContentManagerExchange, setContentManagerExchangeAdditionalMailboxes } from "../../../src/taskpane/redux/modules/contentManagerDirectory";
import { setSearch } from "../../../src/taskpane/redux/modules/search";
import { addWorkingDirectories, setWorkingDirectories } from "../../../src/taskpane/redux/modules/workingDirectory";
import { clearExchangeFolderCaches, getExchangeChildFolders, getExchangeFolders, getMailFolder, getWellKnownFolders } from "../../../src/taskpane/services/ExchangeService";
import generalModule from "../../../utilities/GeneralModule";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { logger } from "../../../utilities/logger";
import { useMailboxPopUpAuth } from "../../Hooks/useMailboxPopUpAuth";
import { apiGetAdditionalMailboxes } from "../../../src/taskpane/services/ApiService";
import { Folder20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/Folder20Regular";


interface IExchangeDirectoryProps {

}

interface IFolderNavLink extends INavLink {
    id: string;
    mailboxId: string;
    isPlaceholder?: boolean;
}

interface IMailboxInfo {
    index: number;
    mailboxAddress: string;
    folders: IMailFolder[];
    wellknownFolders?: IWellKnownFolder[];
}

const ExchangeDirectory = ({ }: IExchangeDirectoryProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const account = useSelector((state: RootState) => state.user?.account);
    const exchange = useSelector((state: RootState) => state.contentManagerDirectory?.exchange);
    const contentManagerFilterValue = useSelector((state: RootState) => state.contentManagerDirectory?.filterValue);
    const searchValue = useSelector((state: RootState) => state.search?.value)?.value;
    const additionalMailboxes = useSelector((state: RootState) => state.contentManagerDirectory?.exchange?.additionalMailboxes);
    const reloadToggle = useSelector((state: RootState) => state.reload.toggle);

    const { isMailboxTokenAvailable, isMailboxTokenRequestInProgress, requestMailboxToken } = useMailboxPopUpAuth();

    const dispatch = useDispatch();

    const [navLinkGroups, setNavLinkGroups] = React.useState<INavLinkGroup[]>([]);
    const [wellKnownFolders, setWellKnownFolders] = React.useState<IWellKnownFolder[]>([]);
    const [isLoadingFolders, setIsLoadingFolders] = React.useState(true);

    const isFirstLoad = React.useRef(true);
    // True once a folder has been selected this mount (instant inbox select, the
    // directory-load fallback, or a user click); stops the slower paths from
    // overriding a selection that already happened.
    const hasSelectedInitialFolder = React.useRef(false);
    const isInboxQuickSelectStarted = React.useRef(false);
    const loadingFolderIds = React.useRef<Set<string>>(new Set());

    const createExchangePageSettings = (folderId: string, folderName: string): IPageSettings => {
        return {
            type: ComponentTypes.Exchange,
            siteId: "",
            siteDisplayName: folderName,
            webUrl: "",
            directory: "",
            DocumentLibrary: "",
            displayName: folderName,
            exchangeFolderId: folderId
        };
    };

    const selectFolder = (folderId: string, folderName: string, mailboxAddress: string) => {
        dispatch(setContentManagerExchange({ selectedFolderId: folderId, selectedFolderName: folderName, selectedAccount: mailboxAddress }));
        dispatch(setWorkingDirectories([createExchangePageSettings(folderId, folderName)]));
    };

    // Select the primary inbox through a single cheap folder lookup ("inbox" is a
    // Graph well-known name) so the message list starts loading immediately instead
    // of waiting for the full directory enumeration below. Skipped while a folder
    // filter is active: the filtered nav may not contain the inbox, and the old
    // behavior (auto-select only when inbox is in the fetched list) must hold.
    React.useEffect(() => {
        let isActive: boolean = true;

        if (isMailboxTokenAvailable && account && generalModule.isInConentMangerMode()
            && !contentManagerFilterValue && !isInboxQuickSelectStarted.current) {
            isInboxQuickSelectStarted.current = true;
            (async () => {
                const inboxFolder = await getMailFolder(account.username, "inbox");
                // isActive: a late response must not hijack workingDirectories after the
                // user switched to another section (the body renders by workingDirectories).
                if (inboxFolder?.id && isActive && !hasSelectedInitialFolder.current) {
                    hasSelectedInitialFolder.current = true;
                    selectFolder(inboxFolder.id, inboxFolder.displayName, account.username);
                }
            })();
        }

        return () => {
            isActive = false;
        };
    }, [isMailboxTokenAvailable, account, contentManagerFilterValue]);

    React.useEffect(() => {
        if (isMailboxTokenAvailable && account) {
            (async () => {
                setIsLoadingFolders(true);
                if (additionalMailboxes === undefined) {
                    try {
                        const mailboxes = await apiGetAdditionalMailboxes(account.accountId);
                        dispatch(setContentManagerExchangeAdditionalMailboxes(mailboxes?.value?.filter(x => x?.length > 0) ?? []));
                    } catch (error) {
                        logger(error, "ExchangeDirectory.tsx (getAdditionalMailboxes)");
                        // Fall back to the primary mailbox only; leaving this undefined
                        // would keep the folder loader spinning forever.
                        dispatch(setContentManagerExchangeAdditionalMailboxes([]));
                    }
                }
            })();
        } else {
            setIsLoadingFolders(false);
        }
    }, [isMailboxTokenAvailable, additionalMailboxes, account]);

    React.useEffect(() => {
        let isActive: boolean = true;

        if (additionalMailboxes !== undefined) {
            (async () => {
                const navLinkGroups: INavLinkGroup[] = [];
                let wellknownList: IWellKnownFolder[] = [];

                const allMailboxes = [account.username, ...additionalMailboxes];

                let mailboxInfoList: IMailboxInfo[] = await Promise.all(
                    allMailboxes.map(async (mailbox, index) => {
                        // Sequential on purpose: Outlook allows only 4 concurrent requests
                        // per mailbox and Graph dispatches up to 4 batch steps at a time,
                        // so running the folder request alongside the well-known-folders
                        // batch throttles one of them.
                        const folders = await fetchFolders(mailbox);
                        const wellknown = await getWellKnownFolders(mailbox);

                        return {
                            index: index,
                            mailboxAddress: mailbox as string,
                            folders: folders,
                            wellknownFolders: wellknown
                        } as IMailboxInfo;
                    })
                );

                if (!isActive) {
                    return;
                }

                if (mailboxInfoList?.length > 0) {
                    mailboxInfoList = mailboxInfoList
                        .sort((a, b) => a.index - b.index)
                        .filter(mailboxInfo => mailboxInfo.folders?.length > 0);

                    for (let index = 0; index < mailboxInfoList.length; index++) {
                        const mailboxInfo: IMailboxInfo = mailboxInfoList[index];

                        // Fallback for the instant inbox select above (e.g. its request
                        // failed); never override a selection that already happened.
                        if (mailboxInfo.index === 0 && !hasSelectedInitialFolder.current && generalModule.isInConentMangerMode()) {
                            const inboxWellKnown = mailboxInfo.wellknownFolders?.find(x => x.name === "inbox");
                            const inboxFolder = inboxWellKnown ? mailboxInfo.folders?.find(x => x.id === inboxWellKnown.id) : undefined;
                            if (inboxFolder) {
                                hasSelectedInitialFolder.current = true;
                                selectFolder(inboxFolder.id, inboxFolder.displayName, mailboxInfo.mailboxAddress);
                            }
                        }

                        if (mailboxInfo.wellknownFolders) {
                            wellknownList = wellknownList.concat(mailboxInfo.wellknownFolders);
                        }

                        navLinkGroups.push(createNavBarGroup(mailboxInfo.mailboxAddress, mailboxInfo.folders));
                    }
                }

                setWellKnownFolders(wellknownList);
                setIsLoadingFolders(false);
                setNavLinkGroups(navLinkGroups);
                isFirstLoad.current = false;
            })();
        }

        return () => {
            isActive = false;
        };
    }, [additionalMailboxes])

    // Refresh must surface folders created/renamed in Outlook — and mailboxes
    // granted or lost — since the session state was written. Clearing the folder
    // caches and resetting the mailbox list to undefined makes Refresh flow
    // causally through state instead of effect declaration order: the reset
    // re-runs the mailbox fetch above, whose result re-runs the enumeration
    // effect above against the now-empty caches. This also gives a failed
    // mailbox fetch (which falls back to []) its retry path.
    const isFirstReloadToggle = React.useRef(true);
    React.useEffect(() => {
        if (isFirstReloadToggle.current) {
            isFirstReloadToggle.current = false;
            return;
        }
        clearExchangeFolderCaches();
        dispatch(setContentManagerExchangeAdditionalMailboxes(undefined));
    }, [reloadToggle]);

    React.useEffect(() => {
        let isActive: boolean = true;

        // additionalMailboxes === undefined means a Refresh reset is mid-flight;
        // the enumeration effect above re-runs when the fresh list lands, so a
        // partial run here (primary mailbox only) would just be thrown away.
        if (!isFirstLoad.current && isMailboxTokenAvailable && additionalMailboxes !== undefined) {
            (async () => {
                const navLinkGroups: INavLinkGroup[] = [];
                setIsLoadingFolders(true);

                let allMailboxes = [account.username];
                if (additionalMailboxes?.length > 0) {
                    allMailboxes = allMailboxes.concat(additionalMailboxes);
                }

                let mailboxInfoList: IMailboxInfo[] = await Promise.all(
                    allMailboxes.map(async (mailbox, index) => {
                        return {
                            index: index,
                            mailboxAddress: mailbox as string,
                            folders: await fetchFolders(mailbox)
                        } as IMailboxInfo;
                    })
                );

                // Filtered fetches bypass the session cache, so rapid filter
                // typing produces overlapping in-flight enumerations; a stale
                // response resolving last must not overwrite the nav with results
                // for a filter the user already replaced (nor flip the loader off
                // while the newer fetch is still running).
                if (!isActive) {
                    return;
                }

                if (mailboxInfoList?.length > 0) {
                    mailboxInfoList = mailboxInfoList
                        .sort((a, b) => a.index - b.index)
                        .filter(mailboxInfo => mailboxInfo.folders?.length > 0);

                    for (let index = 0; index < mailboxInfoList.length; index++) {
                        const mailboxInfo: IMailboxInfo = mailboxInfoList[index];
                        navLinkGroups.push(createNavBarGroup(mailboxInfo.mailboxAddress, mailboxInfo.folders));
                    }
                }

                setNavLinkGroups(navLinkGroups);
                setIsLoadingFolders(false);
            })();
        }

        return () => {
            isActive = false;
        };
    }, [contentManagerFilterValue, searchValue, isMailboxTokenAvailable, additionalMailboxes]);

    const fetchFolders = async (accountUsername: string) => {
        try {
            let filter = "";
            if (generalModule.isInConentMangerMode()) {
                filter = contentManagerFilterValue ? contentManagerFilterValue : "";
            } else {
                filter = searchValue ? searchValue : "";
            }

            return await getExchangeFolders(accountUsername, filter);
        } catch (error) {
            logger(error, "ExchangeDirectory.tsx (fetchFolders)");
            return [];
        }
    }

    const createNavBarGroup = (accountName: string, folders: IMailFolder[]) => {
        const links = createNavFromFolders(folders, accountName);
        return {
            name: accountName,
            links
        };
    }

    const createPlaceholderLink = (parentFolderId: string, accountName: string): IFolderNavLink => {
        return {
            id: `${parentFolderId}-placeholder`,
            mailboxId: accountName,
            key: `${parentFolderId}-placeholder`,
            name: languageStrings.Loading,
            url: "#",
            disabled: true,
            isPlaceholder: true,
            links: []
        };
    }

    const createNavFromFolders = (folders: IMailFolder[], accountName: string): IFolderNavLink[] => {
        const links: IFolderNavLink[] = [];

        if (folders.length > 0) {
            for (let index = 0; index < folders.length; index++) {
                const folder = folders[index];
                const newFolderLink: IFolderNavLink = {
                    id: folder.id,
                    mailboxId: accountName,
                    key: folder.id,
                    name: folder.displayName,
                    url: "#",
                    // Children load lazily on chevron expand. The placeholder child makes
                    // the chevron render from childFolderCount, which Graph returns on
                    // every folder, so a failed child fetch can never hide expandability.
                    links: folder.childFolderCount > 0 ? [createPlaceholderLink(folder.id, accountName)] : []
                };

                links.push(newFolderLink);
            }
        }

        return links;
    }

    // Applies an update to the link with the given folder id in the CURRENT nav state.
    // Looking the link up by id (instead of mutating the object captured before an
    // await) keeps the update effective even when the filter/search effect rebuilt
    // the tree with fresh objects while a child fetch was in flight. Nav tracks
    // expansion by mutating the link objects it was handed, so the update mutates in
    // place and re-renders via a shallow-copied groups array, preserving every other
    // link's expansion state.
    const updateFolderLink = (folderId: string, update: (link: IFolderNavLink) => void) => {
        setNavLinkGroups(groups => {
            const visit = (links: INavLink[]): boolean => {
                for (const navLink of links ?? []) {
                    if ((navLink as IFolderNavLink).id === folderId) {
                        update(navLink as IFolderNavLink);
                        return true;
                    }
                    if (visit(navLink.links)) {
                        return true;
                    }
                }
                return false;
            };

            for (const group of groups) {
                if (visit(group.links)) {
                    break;
                }
            }
            return [...groups];
        });
    }

    const _onLinkExpandClick = (_ev?: React.MouseEvent<HTMLElement>, item?: INavLink) => {
        const link = item as IFolderNavLink;
        // Fetch whenever the children are still the loading placeholder. Deliberately
        // NOT gated on isExpanded: that would depend on Fluent invoking this callback
        // before toggling the flag, and the fetch is already idempotent via the
        // placeholder check, loadingFolderIds, and the service cache.
        const needsLoad = link?.links?.some(childLink => (childLink as IFolderNavLink).isPlaceholder);
        if (!needsLoad || loadingFolderIds.current.has(link.id)) {
            return;
        }
        loadingFolderIds.current.add(link.id);

        (async () => {
            try {
                const childFolders = await getExchangeChildFolders(link.mailboxId, link.id);
                updateFolderLink(link.id, currentLink => {
                    if (childFolders) {
                        // An empty result clears the placeholder (all children hidden),
                        // removing the chevron.
                        currentLink.links = createNavFromFolders(childFolders, link.mailboxId);
                    } else {
                        // Fetch failed: keep the placeholder but collapse the node so the
                        // failure is visible and the next chevron click retries.
                        currentLink.isExpanded = false;
                    }
                });
            } finally {
                loadingFolderIds.current.delete(link.id);
            }
        })();
    }

    const _onLinkClick = React.useCallback((ev?: React.MouseEvent<HTMLElement, MouseEvent>, item?: IFolderNavLink) => {
        if (item?.isPlaceholder) {
            ev?.preventDefault();
            ev?.stopPropagation();
            return;
        }

        // Any explicit navigation means the automatic inbox selection must not fire anymore.
        hasSelectedInitialFolder.current = true;

        if (exchange?.selectedFolderId !== item?.id) {
            if (generalModule.isInConentMangerMode()) {
                selectFolder(item.id, item.name, item.mailboxId);
            } else {
                dispatch(addWorkingDirectories([createExchangePageSettings(item.id, item.name)]));
                dispatch(setContentManagerExchange({ selectedFolderId: item.id, selectedFolderName: item.name, selectedAccount: item.mailboxId }));
            }

            if (searchValue) {
                dispatch(setSearch(""));
            }
        }
        ev?.preventDefault();
        ev?.stopPropagation();
    }, [exchange?.selectedFolderId, searchValue]);

    return (
        <>
            {!isMailboxTokenAvailable && !isMailboxTokenRequestInProgress &&
                <div style={{ textAlign: "center", paddingTop: "15px" }}>
                    <DefaultButton onClick={requestMailboxToken} text={languageStrings.RequestAccess} />
                </div>
            }
            {(isLoadingFolders || isMailboxTokenRequestInProgress) && <Loader label={languageStrings.Loading} />}
            {isMailboxTokenAvailable && !isMailboxTokenRequestInProgress && !isLoadingFolders && <Nav
                selectedKey={exchange.selectedFolderId}
                groups={navLinkGroups}
                onLinkClick={_onLinkClick}
                onLinkExpandClick={_onLinkExpandClick}
                onRenderLink={(link: IFolderNavLink) => {
                    if (link.isPlaceholder) {
                        return <span style={{ fontStyle: "italic", paddingLeft: "5px" }}>{link.name}</span>;
                    }
                    const wellKnownFolder = wellKnownFolders.find(x => x.id === link.id);
                    const folderIcon = wellKnownFolder ? wellKnownFolder.icon : <Folder20Regular />;
                    return (
                        <div>
                            <Stack horizontal tokens={{ childrenGap: 5 }} styles={{ root: { alignItems: "center" } }}>
                                {folderIcon}
                                <span style={{ paddingLeft: "5px" }}>{link.name}</span>
                            </Stack>
                        </div>
                    );
                }}
            />}
        </>
    )
}

export default React.memo(ExchangeDirectory);